/**
 * The palace's frame catalogue: every save hangs in one of fourteen mouldings,
 * chosen by a stable hash of its id so a piece keeps its frame for the whole
 * visit — rooms stream in and out constantly, and a frame that reshuffled on
 * every reload would make the building feel like it was redecorating behind
 * your back.
 *
 * Pure data, no three.js: `palaceLayout` needs the footprint at plan time (so
 * the hang can pack around a baroque frame's corner blocks) and `palaceBuild`
 * needs the bars at build time, and neither should drag the other's imports in.
 *
 * A style is a list of BARS. Every bar is an axis-aligned box:
 *
 *   { dx, dy, dz, sw, sh, sd, mat?, color? }
 *
 * with dx right and dy up in the frame's own plane, and **dz toward the
 * viewer**. The artwork sits at dz = 0 and the wall face is at dz = -WALL_OFF
 * (-0.06), so `dz - sd/2 >= -0.055` for every bar or the moulding buries
 * itself in the plaster.
 *
 * The palace has no shadow maps, so depth cannot read as cast shadow — it
 * reads only as face-normal shading against the low key light and the roaming
 * lamps. That is why nearly every style below steps at least one bar in dz: a
 * proud ring's top face lands in a different tone band than the flat ring
 * behind it, and across a room that tone step *is* the profile.
 */

import { PALACE, hashString } from './constants.js';

// Material buckets. Colour is free — three.js multiplies `instanceColor` into
// the material colour, so every style in a bucket shares one draw call no
// matter how many tints it uses. Only roughness/metalness force a split, so
// there are four buckets rather than one per timber.
export const FRAME_MATERIALS = {
  frameMatte: { roughness: 0.72, metalness: 0.0 },
  frameGilt: { roughness: 0.42, metalness: 0.6 },
  frameSteel: { roughness: 0.3, metalness: 0.85 },
  frameEbony: { roughness: 0.34, metalness: 0.25 },
};

// Timbers and metals, as instance colours.
const OAK = 0x5a4632;
const WALNUT = 0x4a3427;
const RAG = 0xd8d2c6; // conservation mount board
const KRAFT = 0xc8b08a; // masking tape
const GOLD = 0x9a7b2f;
const ALUMINIUM = 0xc6cad0;
const BLACK = 0x14100d;

/**
 * A rectangular ring of four bars. `off` is the outward offset of the ring's
 * inner edge from the artwork edge, so a negative `off` is a sight lip that
 * laps over the picture the way a real rabbet does. Extension per side is
 * `off + b`.
 */
const ring = (W, H, b, off, dz, sd) => [
  { dx: 0, dy: H / 2 + off + b / 2, dz, sw: W + 2 * off + 2 * b, sh: b, sd },
  { dx: 0, dy: -(H / 2 + off + b / 2), dz, sw: W + 2 * off + 2 * b, sh: b, sd },
  { dx: -(W / 2 + off + b / 2), dy: 0, dz, sw: b, sh: H + 2 * off, sd },
  { dx: (W / 2 + off + b / 2), dy: 0, dz, sw: b, sh: H + 2 * off, sd },
];

// Four blocks at the artwork's corners, `inset` in from each edge.
const corners = (W, H, inset, size, dz, sd, extra = {}) =>
  [[-1, 1], [1, 1], [-1, -1], [1, -1]].map(([sx, sy]) => ({
    dx: sx * (W / 2 - inset),
    dy: sy * (H / 2 - inset),
    dz,
    sw: size,
    sh: size,
    sd,
    ...extra,
  }));

/**
 * The catalogue. `ext` is the frame's reach past the artwork on each side and
 * is what the hang packs against — get it wrong and neighbouring frames touch.
 * `weight` is the pick share out of the total below: the three plain rectangle
 * mouldings carry a third of the wall between them, which is what keeps the
 * building reading as a gallery instead of a novelty shop.
 */
export const FRAME_STYLES = [
  {
    id: 'plainOak',
    mat: 'frameMatte',
    color: OAK,
    weight: 10,
    ext: { x: 0.07, t: 0.07, b: 0.07 },
    // The flat-face oak section every regional museum has hung since 1950.
    bars: (W, H) => ring(W, H, 0.07, 0, 0, 0.07),
  },
  {
    id: 'walnutBevel',
    mat: 'frameMatte',
    color: WALNUT,
    weight: 8,
    ext: { x: 0.07, t: 0.07, b: 0.07 },
    // A cassetta: wide flat outer band, narrow proud inner lip. Both rings are
    // the same walnut — the two-tone read is pure geometry, which costs nothing.
    bars: (W, H) => [
      ...ring(W, H, 0.032, -0.012, 0.028, 0.05),
      ...ring(W, H, 0.05, 0.02, -0.005, 0.075),
    ],
  },
  {
    id: 'thinBlack',
    mat: 'frameEbony',
    color: BLACK,
    weight: 9,
    ext: { x: 0.022, t: 0.022, b: 0.022 },
    // The contemporary photography frame: 22mm of section and nothing else.
    // Carries a little metalness because a matte black frame on a dark wing
    // wall is not a silhouette, it is an absence — the specular line along the
    // top face is the only thing that draws it.
    bars: (W, H) => ring(W, H, 0.022, 0, 0.004, 0.058),
  },
  {
    id: 'hairlineSteel',
    mat: 'frameSteel',
    color: ALUMINIUM,
    weight: 7,
    ext: { x: 0.016, t: 0.016, b: 0.016 },
    // Nielsen knife-edge: 16mm wide, 90mm deep. Almost no face, all edge, so
    // it reads as a bright line drawn around the picture.
    bars: (W, H) => ring(W, H, 0.016, 0, 0.012, 0.09),
  },
  {
    id: 'deepMat',
    mat: 'frameEbony',
    color: BLACK,
    weight: 6,
    ext: { x: 0.061, t: 0.061, b: 0.061 },
    // Conservation mount: rag board around the image, then a slim frame. The
    // board stands 10mm proud of the card so its inner edge throws a tone line.
    // A museum would give this a 120mm mount; at that reach it would overlap
    // its neighbours, so the board is cut to 45mm.
    bars: (W, H) => [
      ...ring(W, H, 0.045, -0.012, -0.012, 0.045).map((b) => ({ ...b, mat: 'frameMatte', color: RAG })),
      ...ring(W, H, 0.028, 0.033, 0.01, 0.1),
    ],
  },
  {
    id: 'shadowbox',
    mat: 'frameEbony',
    color: BLACK,
    weight: 5,
    ext: { x: 0.05, t: 0.05, b: 0.05 },
    // A 160mm-deep well with the card sunk at the back of it. Four bars for an
    // enormous silhouette — the best profile-per-instance in the catalogue.
    bars: (W, H) => ring(W, H, 0.05, 0, 0.045, 0.16),
  },
  {
    id: 'floatingMount',
    mat: 'frameEbony',
    color: BLACK,
    weight: 7,
    ext: { x: 0.06, t: 0.06, b: 0.06 },
    // Tray frame: a 30mm reveal of bare wall between picture and moulding, so
    // the piece floats and the gap picks up the wing's own wall colour.
    bars: (W, H) => ring(W, H, 0.03, 0.03, 0.02, 0.11),
  },
  {
    id: 'frameless',
    mat: 'frameSteel',
    color: ALUMINIUM,
    weight: 6,
    ext: { x: 0.0175, t: 0.0175, b: 0.0175 },
    // A plexi sandwich in four corner clips. Barely a frame; the glints are
    // the entire read.
    bars: (W, H) => corners(W, H, 0.01, 0.055, 0.018, 0.04),
  },
  {
    id: 'baroqueGilt',
    mat: 'frameGilt',
    color: GOLD,
    weight: 3,
    ext: { x: 0.076, t: 0.076, b: 0.076 },
    // The showstopper: sight lip, wide main moulding, and four heavy corner
    // blocks standing 90mm off the wall. A Louis XIV frame is recognisable by
    // its corners long before any of its carving resolves, so the blocks are
    // where the instances go. They stop flush with the main ring's outer edge
    // — carried out to true proportion they would eat the gutter.
    bars: (W, H) => [
      ...ring(W, H, 0.03, -0.014, 0.032, 0.052),
      ...ring(W, H, 0.06, 0.016, 0, 0.09),
      ...corners(W, H, -0.006, 0.14, 0.03, 0.12),
    ],
  },
  {
    id: 'beadedGilt',
    mat: 'frameGilt',
    color: GOLD,
    weight: 5,
    ext: { x: 0.062, t: 0.062, b: 0.062 },
    // Plain gilt with a bead course running the outer edge. Real beading is a
    // row of spheres; a continuous raised fillet reads the same from the middle
    // of the room and costs four instances instead of forty.
    bars: (W, H) => [
      ...ring(W, H, 0.048, 0, 0, 0.075),
      ...ring(W, H, 0.014, 0.048, 0.045, 0.03),
    ],
  },
  {
    id: 'salonPlaque',
    mat: 'frameMatte',
    color: WALNUT,
    weight: 4,
    ext: { x: 0.055, t: 0.055, b: 0.075 },
    // Royal Academy convention: a deepened bottom rail carrying a brass label.
    // The plate sits *on* the rail rather than hanging below it — below, it
    // reaches 125mm and collides with whatever is hung underneath.
    bars: (W, H) => [
      { dx: 0, dy: H / 2 + 0.0275, dz: 0, sw: W + 0.11, sh: 0.055, sd: 0.07 },
      { dx: -(W / 2 + 0.0275), dy: 0, dz: 0, sw: 0.055, sh: H, sd: 0.07 },
      { dx: (W / 2 + 0.0275), dy: 0, dz: 0, sw: 0.055, sh: H, sd: 0.07 },
      { dx: 0, dy: -(H / 2 + 0.0375), dz: 0, sw: W + 0.11, sh: 0.075, sd: 0.07 },
      {
        dx: 0,
        dy: -(H / 2 + 0.0375),
        dz: 0.043,
        sw: Math.min(0.34, W * 0.55),
        sh: 0.04,
        sd: 0.016,
        mat: 'frameGilt',
        color: GOLD,
      },
    ],
  },
  {
    id: 'tapedPrint',
    mat: 'frameMatte',
    color: KRAFT,
    weight: 4,
    ext: { x: 0, t: 0.019, b: 0.019 },
    // Someone stuck it up with masking tape. The strips straddle the card's top
    // and bottom edges near the corners, covering ~38mm the card's own padding
    // absorbs. Kraft tan rather than white: against a pale wing's wall a white
    // strip disappears completely.
    bars: (W, H) => [1, -1].flatMap((sy) =>
      [-1, 1].map((sx) => ({
        dx: sx * (W / 2 - 0.075),
        dy: sy * (H / 2),
        dz: 0.005,
        sw: 0.15,
        sh: 0.038,
        sd: 0.008,
      })),
    ),
  },
  {
    id: 'pinned',
    mat: 'frameGilt',
    color: GOLD,
    weight: 3,
    ext: { x: 0, t: 0, b: 0 },
    // Four brass drawing pins, no frame at all. It survives at a distance
    // because brass catches the roaming lamps, and four points of light around
    // a bare card is a recognisable image.
    bars: (W, H) => corners(W, H, 0.045, 0.03, 0.014, 0.03),
  },
  {
    id: 'brokenMoulding',
    mat: 'frameGilt',
    color: GOLD,
    weight: 2,
    ext: { x: 0.06, t: 0.06, b: 0.073 },
    // Ruined gilt: three intact sides, a gap right of centre in the bottom
    // rail, and the fragment that fell out of it lying lower and further back.
    // Deliberately off-centre — centred, it stops reading as damage and starts
    // reading as a design.
    bars: (W, H) => [
      { dx: 0, dy: H / 2 + 0.03, dz: 0, sw: W + 0.12, sh: 0.06, sd: 0.075 },
      { dx: -(W / 2 + 0.03), dy: 0, dz: 0, sw: 0.06, sh: H, sd: 0.075 },
      { dx: (W / 2 + 0.03), dy: 0, dz: 0, sw: 0.06, sh: H, sd: 0.075 },
      { dx: -(W * 0.28 + 0.03), dy: -(H / 2 + 0.03), dz: 0, sw: W * 0.44 + 0.06, sh: 0.06, sd: 0.075 },
      { dx: (W * 0.32 + 0.03), dy: -(H / 2 + 0.03), dz: 0, sw: W * 0.36 + 0.06, sh: 0.06, sd: 0.075 },
      { dx: W * 0.02, dy: -(H / 2 + 0.052), dz: -0.018, sw: W * 0.11, sh: 0.042, sd: 0.045 },
    ],
  },
];

const BY_ID = new Map(FRAME_STYLES.map((s) => [s.id, s]));

// Cumulative weights for the pick. Built once; the total is whatever the
// catalogue happens to add up to, so styles can be retuned without a second
// number to keep in step.
const CUMULATIVE = FRAME_STYLES.reduce((acc, s) => {
  acc.push((acc[acc.length - 1] ?? 0) + s.weight);
  return acc;
}, []);
const TOTAL_WEIGHT = CUMULATIVE[CUMULATIVE.length - 1];

/**
 * The frame a save hangs in. A pure function of the id, so it survives room
 * streaming, does not depend on hang order, and can be asked for at plan time
 * and again at build time without threading state between them.
 *
 * Salted with a `frame:` prefix because the bare id hash already seeds layout
 * elsewhere — reused unsalted, a piece's frame would correlate with its size.
 */
export const frameStyleFor = (tweet) => {
  const id = tweet?.id ?? '';
  const r = (hashString(`frame:${id}`) >>> 0) % TOTAL_WEIGHT;
  const idx = CUMULATIVE.findIndex((c) => r < c);
  return FRAME_STYLES[idx];
};

export const frameStyleById = (id) => BY_ID.get(id) ?? FRAME_STYLES[0];

/**
 * The footprint a save actually occupies once framed, for the hang to pack
 * against. `w`/`h` are the outer rectangle — what must not collide — while
 * `aw`/`ah` stay the artwork opening the canvas is drawn into. `offY` carries
 * the two vertically asymmetric styles: the outer rect's centre sits above the
 * picture's when the bottom rail is the deep one.
 *
 * The outer height is capped to the hang band. A frame taller than the band
 * gets refused by every segment and its save falls out of the building
 * entirely, which is a far worse outcome than a card drawn a few percent
 * smaller than its character count asked for.
 */
export const framedFootprint = (tweet, size) => {
  const style = frameStyleFor(tweet);
  const { x, t, b } = style.ext;
  const maxOuter = PALACE.RAIL - PALACE.HANG_LO - 0.1;
  const shrink = Math.min(1, (maxOuter - t - b) / size.h);
  const aw = size.w * shrink;
  const ah = size.h * shrink;
  return {
    tweet,
    hasMedia: size.hasMedia,
    style: style.id,
    aw,
    ah,
    w: aw + 2 * x,
    h: ah + t + b,
    offY: (t - b) / 2,
  };
};
